/**
 * Artisti per gli organizzatori (docs/06-sito-web.md, "Artisti per gli organizzatori"): chi
 * organizza descrive l'evento e cosa cerca, sceglie fino a 5 artisti dall'anagrafica e la richiesta
 * arriva all'artista tramite salentosagreeventi (email, o la redazione su Instagram/Facebook).
 * Se l'artista è interessato, chi organizza riceve la risposta con i suoi contatti e da lì parlano
 * direttamente. Senza zod: si usa anche nel browser (`@sse/schema/booking`).
 */
/** Tipi di artista (dall'anagrafica: tipo dell'esibizione negli annunci). */
export const ARTIST_KINDS = [
    'banda',
    'popolare',
    'band',
    'tribute',
    'cantante',
    'dj',
    'orchestra',
    'comico',
    'attore',
    'strada',
    'ballo',
    'coro',
    'luminarie',
    'fuochi',
    'altro',
];
/** Tipi senza un genere musicale (luminarie, fuochi, comici, attori, artisti di strada). */
export const NO_GENRE_KINDS = [
    'luminarie',
    'fuochi',
    'comico',
    'attore',
    'strada',
];
/**
 * Chi si può seguire sul sito (musica e spettacolo): pagina pubblica, "Segui", filtro degli eventi.
 * Luminarie, fuochi e "altro" restano collegati agli eventi solo per chi organizza.
 */
export const FOLLOWABLE_KINDS = [
    'banda',
    'popolare',
    'band',
    'tribute',
    'cantante',
    'dj',
    'orchestra',
    'comico',
    'attore',
    'strada',
    'ballo',
    'coro',
];
/** Il genere conta se non si è scelto un tipo o se almeno uno dei tipi scelti fa musica. */
export const hasGenres = (kinds) => kinds.length === 0 || kinds.some((k) => !NO_GENRE_KINDS.includes(k));
export const ARTIST_KIND_LABELS = {
    banda: 'Banda o concerto bandistico',
    popolare: 'Musica popolare e pizzica',
    band: 'Band e cover band',
    tribute: 'Tribute band',
    cantante: 'Cantante o musicista',
    dj: 'DJ',
    orchestra: 'Orchestra',
    comico: 'Comico o cabaret',
    attore: 'Attore o attrice',
    strada: 'Artisti di strada',
    ballo: 'Ballo e danza',
    coro: 'Coro',
    luminarie: 'Luminarie',
    fuochi: "Fuochi d'artificio",
    altro: 'Altro',
};
/** Generi, normalizzati all'import dell'anagrafica. */
export const ARTIST_GENRES = [
    'pizzica',
    'popolare',
    'pop',
    'rock',
    'revival',
    'dance',
    'liscio',
    'latino',
    'jazz',
    'reggae',
    'rap',
    'cantautori',
    'neomelodico',
    'classica',
    'bandistico',
    'comicita',
    'bambini',
    'sacra',
];
export const ARTIST_GENRE_LABELS = {
    pizzica: 'Pizzica e taranta',
    popolare: 'Folk e popolare',
    pop: 'Pop e musica italiana',
    rock: 'Rock',
    revival: 'Anni 60-90',
    dance: 'Dance ed elettronica',
    liscio: 'Liscio e balli di gruppo',
    latino: 'Latino',
    jazz: 'Jazz, blues e swing',
    reggae: 'Reggae',
    rap: 'Rap e hip hop',
    cantautori: 'Cantautori',
    neomelodico: 'Neomelodico e napoletana',
    classica: 'Lirica e classica',
    bandistico: 'Bandistico e marce',
    comicita: 'Comicità',
    bambini: 'Per bambini',
    sacra: 'Musica sacra',
};
/** Tipo dell'evento che si organizza. */
export const BOOKING_EVENT_TYPES = [
    'sagra',
    'festa_patronale',
    'concerto',
    'locale',
    'privato',
    'altro',
];
export const BOOKING_EVENT_TYPE_LABELS = {
    sagra: 'Sagra',
    festa_patronale: 'Festa patronale',
    concerto: 'Concerto o festival',
    locale: 'Serata in un locale',
    privato: 'Evento privato (matrimonio, festa)',
    altro: 'Altro',
};
export const BOOKING_VENUES = ['piazza', 'aperto', 'locale'];
export const BOOKING_VENUE_LABELS = {
    piazza: 'In piazza o per strada',
    aperto: "All'aperto (masseria, lido, parco)",
    locale: 'Al chiuso (locale, sala)',
};
export const BOOKING_AUDIENCES = ['lt200', '200_1000', '1000_5000', 'gt5000'];
export const BOOKING_AUDIENCE_LABELS = {
    lt200: 'Fino a 200 persone',
    '200_1000': '200-1.000 persone',
    '1000_5000': '1.000-5.000 persone',
    gt5000: 'Oltre 5.000 persone',
};
/** Fascia di budget facoltativa (i cachet degli artisti non si mostrano). */
export const BOOKING_BUDGETS = ['lt500', '500_1500', '1500_3000', 'gt3000', 'tbd'];
export const BOOKING_BUDGET_LABELS = {
    lt500: 'Fino a 500 €',
    '500_1500': '500-1.500 €',
    '1500_3000': '1.500-3.000 €',
    gt3000: 'Oltre 3.000 €',
    tbd: 'Da definire',
};
/** Artisti per richiesta, richieste al giorno per persona. */
export const BOOKING_MAX_ARTISTS = 5;
export const BOOKING_DAILY_LIMIT = 3;
/**
 * Stato di una proposta (richiesta a un artista): `to_send` = da mandare a mano dalla redazione
 * (nessuna email), `sent` = consegnata, `opened` = l'artista ha aperto la pagina, poi la risposta;
 * `no_reply` dopo 7 giorni senza risposta.
 */
export const PROPOSAL_STATUSES = [
    'to_send',
    'sent',
    'opened',
    'interested',
    'unavailable',
    'optout',
    'no_reply',
];
export const PROPOSAL_STATUS_LABELS = {
    to_send: 'Da inviare (redazione)',
    sent: 'Inviata',
    opened: 'Letta',
    interested: 'Interessato',
    unavailable: 'Non disponibile',
    optout: 'Non vuole richieste',
    no_reply: 'Nessuna risposta',
};
/** Esito dichiarato da chi organizza dopo la data dell'evento. */
export const BOOKING_OUTCOMES = ['hired_here', 'other', 'cancelled'];
export const BOOKING_OUTCOME_LABELS = {
    hired_here: 'Ha ingaggiato un artista trovato qui',
    other: 'Ha scelto altro',
    cancelled: "L'evento non si fa",
};
//# sourceMappingURL=booking.js.map